import { useMemo, useState } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Banknote,
  Coins,
  HandCoins,
  ScrollText,
  ShieldCheck,
  Undo2,
  X,
} from "lucide-react";
import DataTable, { Column } from "@/components/ui/DataTable";
import OrderDetailsDrawer from "@/components/order/OrderDetailsDrawer";
import ExpenseDetailsDrawer from "@/components/expenses/ExpenseDetailsDrawer";
import LedgerEntryDrawer from "./LedgerEntryDrawer";
import {
  getLedgerReasonLabel,
  resolveLedgerExpenseUuid,
  resolveLedgerOrder,
} from "./ledgerUtils";
import { usePermissions } from "@/hooks/usePermissions";
import { useGetSessionLedgerQuery } from "@/redux/services/pos/registerSession.api";
import {
  SessionLedgerEntry,
  SessionLedgerSubType,
} from "@/redux/services/pos/pos.type";
import { cn, displayPrice } from "@/lib/utils";

interface SessionLedgerTableProps {
  sessionId: string;
  currency: string;
}

interface SelectedOrder {
  id: string;
  number: string;
}

interface SubTypeMeta {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  tone: "positive" | "negative" | "neutral";
}

const SUBTYPE_META: Record<SessionLedgerSubType, SubTypeMeta> = {
  cash: { label: "Encaissement", icon: Banknote, tone: "positive" },
  payment_tip: { label: "Pourboire encaissé", icon: Coins, tone: "positive" },
  pay_in: { label: "Approvisionnement", icon: ArrowDownToLine, tone: "positive" },
  pay_out: { label: "Sortie de caisse", icon: ArrowUpFromLine, tone: "negative" },
  cash_drop: { label: "Dépôt coffre", icon: ShieldCheck, tone: "negative" },
  tip_in: { label: "Pourboires reçus", icon: Coins, tone: "positive" },
  tip_out: { label: "Pourboires retirés", icon: HandCoins, tone: "negative" },
  refund: { label: "Remboursement", icon: Undo2, tone: "negative" },
  correction: { label: "Correction", icon: ScrollText, tone: "neutral" },
};

const toIsoOrUndefined = (value: string): string | undefined => {
  if (!value) return undefined;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

const FILTERS: { value: "" | SessionLedgerSubType; label: string }[] = [
  { value: "", label: "Tout" },
  { value: "cash", label: "Encaissements" },
  { value: "payment_tip", label: "Pourboires encaissés" },
  { value: "pay_in", label: "Approvisionnements" },
  { value: "pay_out", label: "Sorties" },
  { value: "cash_drop", label: "Dépôts coffre" },
  { value: "tip_in", label: "Pourboires reçus" },
  { value: "tip_out", label: "Pourboires retirés" },
  { value: "refund", label: "Remboursements" },
  { value: "correction", label: "Corrections" },
];

const SessionLedgerTable = ({ sessionId, currency }: SessionLedgerTableProps) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [subType, setSubType] = useState<"" | SessionLedgerSubType>("");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [selectedOrder, setSelectedOrder] = useState<SelectedOrder | null>(null);
  const [selectedExpenseId, setSelectedExpenseId] = useState<string | null>(
    null,
  );
  const [selectedEntry, setSelectedEntry] = useState<SessionLedgerEntry | null>(
    null,
  );

  const { can } = usePermissions();
  const canViewExpenses = can("expenses.view");

  const fromIso = toIsoOrUndefined(dateFrom);
  const toIso = toIsoOrUndefined(dateTo);
  const hasDateFilter = Boolean(dateFrom || dateTo);

  const queryArgs = useMemo(
    () => ({
      sessionUuid: sessionId,
      query: {
        page,
        limit: pageSize,
        ...(subType
          ? subType === "cash" || subType === "payment_tip"
            ? { kind: "payment" as const, subType }
            : { kind: "movement" as const, subType }
          : {}),
        ...(fromIso ? { "occurredAt[after]": fromIso } : {}),
        ...(toIso ? { "occurredAt[before]": toIso } : {}),
      },
    }),
    [sessionId, page, pageSize, subType, fromIso, toIso],
  );

  const { data, isFetching } = useGetSessionLedgerQuery(queryArgs);

  const columns: Column[] = [
    {
      key: "subType",
      label: "Type",
      formatter: (_v, row: SessionLedgerEntry) => {
        const meta = SUBTYPE_META[row.subType];
        const Icon = meta?.icon ?? Banknote;
        return (
          <div className="flex items-center gap-2">
            <span
              className={cn(
                "flex h-7 w-7 items-center justify-center rounded-md flex-shrink-0",
                meta?.tone === "positive"
                  ? "bg-success/10 text-success"
                  : meta?.tone === "negative"
                  ? "bg-destructive/10 text-destructive"
                  : "bg-muted text-muted-foreground",
              )}
            >
              <Icon className="h-3.5 w-3.5" />
            </span>
            <span className="text-sm font-medium">
              {meta?.label ?? row.subType}
            </span>
          </div>
        );
      },
    },
    {
      key: "occurredAt",
      label: "Date",
      formatter: (v: string) =>
        v ? format(new Date(v), "dd MMM yyyy · HH:mm", { locale: fr }) : "—",
    },
    {
      key: "actor",
      label: "Par",
      formatter: (v: SessionLedgerEntry["actor"]) => v?.fullName ?? "—",
    },
    {
      key: "order",
      label: "Commande / Raison",
      formatter: (_v, row: SessionLedgerEntry) => {
        // Any entry can reference an order — payments, and refunds/movements
        // tied to one. Fall back to the order stored on the movement metadata.
        const order = resolveLedgerOrder(row);
        if (order) {
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedOrder({
                  id: order.id,
                  number: order.orderNumber,
                });
              }}
              className="font-semibold text-primary hover:underline"
            >
              #{order.orderNumber}
            </button>
          );
        }
        const reasonLabel = getLedgerReasonLabel(row.reason);
        if (!reasonLabel) return "—";
        // Expense-linked movements open the expense, mirroring the order link.
        const expenseUuid = resolveLedgerExpenseUuid(row);
        if (expenseUuid && canViewExpenses) {
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedExpenseId(expenseUuid);
              }}
              className="block max-w-[220px] truncate text-left font-semibold text-primary hover:underline"
              title={reasonLabel}
            >
              {reasonLabel}
            </button>
          );
        }
        return (
          <span
            className="block max-w-[220px] truncate text-sm text-card-foreground"
            title={reasonLabel}
          >
            {reasonLabel}
          </span>
        );
      },
    },
    {
      key: "signedAmount",
      label: "Montant",
      formatter: (v: number) => (
        <span
          className={cn(
            "font-semibold tabular-nums",
            v > 0
              ? "text-success"
              : v < 0
              ? "text-destructive"
              : "text-card-foreground",
          )}
        >
          {v > 0 ? "+" : v < 0 ? "−" : ""}
          {displayPrice(Math.abs(v), currency)}
        </span>
      ),
    },
  ];

  const totalCount = data?.count ?? 0;

  return (
    <div className="dash__card p-6">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold">Mouvement de caisse</h3>
          <p className="text-sm text-muted-foreground">
            {totalCount} entrée{totalCount > 1 ? "s" : ""} sur cette session
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3">
        {FILTERS.map((f) => (
          <button
            key={f.value || "all"}
            type="button"
            onClick={() => {
              setSubType(f.value);
              setPage(1);
            }}
            className={cn(
              "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
              subType === f.value
                ? "border-primary bg-primary/10 text-primary"
                : "border-border text-muted-foreground hover:border-primary/40 hover:text-card-foreground",
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="flex flex-col">
          <label
            htmlFor="ledgerFrom"
            className="text-xs font-medium text-muted-foreground mb-1"
          >
            Depuis
          </label>
          <input
            id="ledgerFrom"
            type="datetime-local"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border border-border px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        <div className="flex flex-col">
          <label
            htmlFor="ledgerTo"
            className="text-xs font-medium text-muted-foreground mb-1"
          >
            Jusqu'à
          </label>
          <input
            id="ledgerTo"
            type="datetime-local"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border border-border px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        {hasDateFilter && (
          <button
            type="button"
            onClick={() => {
              setDateFrom("");
              setDateTo("");
              setPage(1);
            }}
            className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-card-foreground pb-1.5"
          >
            <X className="h-3.5 w-3.5" />
            Réinitialiser
          </button>
        )}
      </div>

      <DataTable
        data={data?.results ?? []}
        columns={columns}
        loading={isFetching}
        totalCount={totalCount}
        currentPage={page}
        pageSize={pageSize}
        searchable={false}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        onRowClick={(row) => setSelectedEntry(row as SessionLedgerEntry)}
        emptyMessage="Aucun mouvement enregistré"
      />

      <LedgerEntryDrawer
        entry={selectedEntry}
        currency={currency}
        onClose={() => setSelectedEntry(null)}
        onOpenOrder={(order) => {
          setSelectedEntry(null);
          setSelectedOrder({ id: order.id, number: order.orderNumber });
        }}
        onOpenExpense={
          canViewExpenses
            ? (expenseUuid) => {
                setSelectedEntry(null);
                setSelectedExpenseId(expenseUuid);
              }
            : undefined
        }
      />

      {selectedOrder && (
        <OrderDetailsDrawer
          orderId={selectedOrder.id}
          orderNumber={selectedOrder.number}
          isOpen={!!selectedOrder}
          onClose={() => setSelectedOrder(null)}
        />
      )}

      <ExpenseDetailsDrawer
        isOpen={!!selectedExpenseId}
        expenseId={selectedExpenseId}
        onClose={() => setSelectedExpenseId(null)}
      />
    </div>
  );
};

export default SessionLedgerTable;
